import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Divider,
  Grid,
  Paper,
  useTheme,
  useMediaQuery,
  InputAdornment
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import BusinessIcon from '@mui/icons-material/Business';
import CategoryIcon from '@mui/icons-material/Category';
import DescriptionIcon from '@mui/icons-material/Description';
import PeopleIcon from '@mui/icons-material/People';
import StarIcon from '@mui/icons-material/Star';
import { useFormContext } from './FormContext';

const Page1Company: React.FC = () => {
  const { formData, errors, handleChange } = useFormContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Adiciona "*" para campos obrigatórios no label
  const requiredLabel = (label: string) => (
    <Box component="span">
      {label} <Box component="span" sx={{ color: 'error.main' }}>*</Box>
    </Box>
  );

  return (
    <Box sx={{ 
      animation: 'fadeIn 0.5s ease-in-out',
      '@keyframes fadeIn': {
        '0%': {
          opacity: 0,
          transform: 'translateY(10px)'
        },
        '100%': {
          opacity: 1,
          transform: 'translateY(0)'
        },
      }
    }}>
      <Paper elevation={0} sx={{ 
        mb: 4, 
        p: 2, 
        borderRadius: 2,
        bgcolor: theme => alpha(theme.palette.primary.light, 0.03),
        border: '1px solid',
        borderColor: 'divider'
      }}>
        <Typography variant="h6" gutterBottom sx={{ 
          color: "primary.main",
          display: 'flex',
          alignItems: 'center',
          fontWeight: 600
        }}>
          <BusinessIcon sx={{ mr: 1 }} />
          Seção 1: A Identidade da Sua Empresa
        </Typography>
        <Typography variant="body2" gutterBottom sx={{ 
          mb: 2, 
          fontStyle: 'italic',
          color: 'text.secondary',
          pl: 4
        }}>
          Esta seção nos ajuda a entender o DNA do seu negócio.
        </Typography>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            id="businessName"
            label={requiredLabel("Nome da Empresa")}
            name="businessName"
            value={formData.businessName}
            onChange={handleChange}
            error={!!errors.businessName}
            helperText={errors.businessName || ""}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessIcon color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                },
                '&.Mui-focused': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }
              }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            id="businessIndustry"
            label={requiredLabel("Qual é o seu ramo de atividade?")}
            name="businessIndustry"
            value={formData.businessIndustry}
            onChange={handleChange}
            error={!!errors.businessIndustry}
            helperText={errors.businessIndustry || "Ex: restaurante, salão de beleza, consultoria de marketing, loja de roupas, etc."}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CategoryIcon color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                },
                '&.Mui-focused': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }
              }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            id="businessDescription"
            label={requiredLabel("Descreva seu negócio em uma ou duas frases")}
            name="businessDescription"
            value={formData.businessDescription}
            onChange={handleChange}
            error={!!errors.businessDescription}
            helperText={errors.businessDescription || "Como você se apresentaria para um novo cliente?"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <DescriptionIcon color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                },
                '&.Mui-focused': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }
              }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            id="targetAudience"
            label="Quem é o seu público-alvo principal?"
            name="targetAudience"
            value={formData.targetAudience}
            onChange={handleChange}
            helperText="Ex: jovens universitários, mães de primeira viagem, empresários locais, etc."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PeopleIcon color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                },
                '&.Mui-focused': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }
              }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            id="businessDifferentials"
            label={requiredLabel("Quais são os 3 principais diferenciais da sua empresa?")}
            name="businessDifferentials"
            multiline
            rows={isMobile ? 4 : 3}
            value={formData.businessDifferentials}
            onChange={handleChange}
            error={!!errors.businessDifferentials}
            helperText={errors.businessDifferentials || "O que faz você ser melhor que a concorrência? Ex: preço, qualidade, atendimento personalizado, entrega rápida."}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <StarIcon color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                },
                '&.Mui-focused': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }
              }
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Page1Company;